'use client';

import { useState, useEffect } from 'react';
import useSWR from 'swr';
import { useCartStore } from 'app/store/cart';
import { postCartCalculate } from 'app/apis/cart';
import { CACHE_QUERY_KEY } from 'app/constants';
import { type CartProductCardType } from 'app/components/ProductCard';

function formatClientCartItem(cartItem: CartItem): CartProductCardType {
    return {
        id: cartItem.id,
        productId: cartItem.product_id,
        specDetailId: cartItem.spec_detail_id,
        name: cartItem.name,
        maxLimit: cartItem.max_limit,
        price: cartItem.price,
        discountedPrice: cartItem.discounted_price,
        quantity: cartItem.quantity,
        specInfo: cartItem.spec_info,
        message: cartItem.message,
        status: cartItem.status,
        alertInfo: cartItem.alert_info,
        image: cartItem.image,
        createDatetime: cartItem.create_datetime,
        hasGiveaway: cartItem.has_giveaway,
        giveaways: cartItem.giveaways,
        promoId: cartItem.promo_id,
        badge: cartItem.badge,
    };
}

const postCartCalculateFetcher = async ([, shipping_profile_id, subdivision, coupon_id]: [
    string,
    string,
    string,
    string,
]) => {
    if (!shipping_profile_id || !subdivision) {
        return null;
    }

    return postCartCalculate({ shipping_profile_id, subdivision, coupon_id });
};

interface UseCartCalculateProps {
    subdivision: string | null;
    shipping_profile_id: string | null;
    coupon_id?: string | null;
    onError?: () => void;
    hasItems: boolean;
    setHasItems: (hasItems: boolean) => void;
}

export function useCartCalculate(props: UseCartCalculateProps) {
    const {
        shipping_profile_id,
        coupon_id = '',
        subdivision,
        onError,
        setHasItems,
    } = props;

    const [isFetching, setIsFetching] = useState(true);

    const [calculateResult, setCalculateResult] = useState<CartCalculateResult | null>(
        null
    );

    const { isLoading, isValidating, mutate } = useSWR(
        [CACHE_QUERY_KEY.calculate, shipping_profile_id, subdivision, coupon_id],
        postCartCalculateFetcher,
        {
            onError,
            onSuccess: (resp) => {
                if (resp && 'data' in resp && resp.data) {
                    setCalculateResult(resp.data);
                } else {
                    setCalculateResult(null);
                }

                setIsFetching(false);
            },
        }
    );

    const [isCalculating, setCalculating] = useState(true);

    const {
        originalCartItems,
        completedCartItems,
        completedShoppingDetail,
        updateCompletedShoppingDetail,
        updateCart,
    } = useCartStore();

    useEffect(() => {
        if (!calculateResult) {
            setIsFetching(false);
            setHasItems(false);
            updateCart([], []);
            updateCompletedShoppingDetail({} as CartCalculateResult);

            return;
        }

        const originalCart = calculateResult.cart_results;

        const _completedCartItems = originalCart.map((cartItem) =>
            formatClientCartItem(cartItem)
        );

        updateCart(originalCart, _completedCartItems);

        updateCompletedShoppingDetail(calculateResult);
    }, [calculateResult, setHasItems, updateCart, updateCompletedShoppingDetail]);

    useEffect(() => {
        setCalculating(isValidating || isLoading);
    }, [isValidating, isLoading]);

    const state = {
        isFetching,
        originalCartItems,
        completedCartItems,
        completedShoppingDetail,
        isCalculating,
    };

    const actions = {
        setCalculating,
        mutate,
        setIsFetching,
    };

    return {
        state,
        actions,
    };
}
